import Papa from "papaparse"
import isInvalidEmail from "./isInvalidEmail"
import {CSVErrors} from "../enums/CsvErrors"

export interface ParseBulkManagersBudgetsResult {
  success: boolean
  error?: CSVErrors
  validRows: string[][]
  totalInvalidRows: number
  totalRows: number
}

export interface ParseBulkManagersBudgetsCallbacks {
  onSuccess: (result: ParseBulkManagersBudgetsResult) => void
  onError: (error: CSVErrors) => void
  onTracking: (event: string) => void
}

const MAX_ROWS = 1000
export function groupAssignmentsByBudget(validRows: string[][], unassignedLabel: string) {
  const grouped: Record<string, {uuid: string; amount: number; email: string}[]> = {}
  let totalToPay = 0
  validRows.forEach((row) => {
    const [uuid, email, , , budgetName, amount] = row
    totalToPay += Number(amount)
    const budget = budgetName || unassignedLabel // Default to unassigned if budgetName is empty
    if (!grouped[budget]) {
      grouped[budget] = []
    }
    grouped[budget].push({uuid, email, amount: Number(amount)})
  })

  // Convert to desired array format
  const assignments = Object.entries(grouped).map(([budget, users]) => ({
    budget,
    users
  }))
  return {assignments, totalToPay}
}
export function parseBulkManagersBudgets(uploadedFile: File, callbacks: ParseBulkManagersBudgetsCallbacks): void {
  const reader = new FileReader()
  reader.onload = () => {
    const csvContent = reader.result as string
    parseBulkManagersBudgetsCsv(csvContent, callbacks)
  }
  reader.onerror = () => {
    callbacks.onError(CSVErrors.INVALID_TYPE)
  }
  reader.readAsText(uploadedFile)
}
export function parseBulkManagersBudgetsCsv(csvContent: string, callbacks: ParseBulkManagersBudgetsCallbacks): void {
  let columnsLength: number
  const parsedRows: string[][] = []
  let uniqRows: string[][] = []
  const invalidRows: string[][] = []

  Papa.parse<string[][]>(csvContent, {
    header: false,
    skipEmptyLines: true,
    delimiter: ",",
    step: (row) => {
      const {
        data: [currentRow]
      } = row

      columnsLength = currentRow.length
      const [uuid, email, fullName, teamNames, budgetName, rpAmount] = currentRow
      if (
        !uuid ||
        !email ||
        !fullName ||
        !teamNames ||
        !rpAmount ||
        isInvalidEmail(email) ||
        !/^-?\d+$/.test(rpAmount) ||
        Number(rpAmount) <= 0
      ) {
        invalidRows.push(currentRow)
        return
      }

      parsedRows.push(currentRow)
      uniqRows = Array.from(new Set(parsedRows.map((parsedRow) => JSON.stringify(parsedRow)))).map((rowStr) => JSON.parse(rowStr))
    },
    complete: () => {
      if (uniqRows.length > MAX_ROWS) {
        callbacks.onTracking("CsvFormatError")
        callbacks.onError(CSVErrors.MAX_ROWS_EXCEEDED)
        return
      }

      if (columnsLength !== 6) {
        callbacks.onTracking("CsvFormatError")
        callbacks.onError(CSVErrors.MULTIPLE_COLUMNS)
        return
      }

      const result: ParseBulkManagersBudgetsResult = {
        success: true,
        validRows: uniqRows,
        totalInvalidRows: invalidRows.length,
        totalRows: uniqRows.length + invalidRows.length
      }

      callbacks.onSuccess(result)
      if (result.validRows.length !== result.totalRows) {
        callbacks.onTracking("CsvFormatError")
        callbacks.onError(CSVErrors.EMAIL_ERRORS)
        return
      }

      callbacks.onTracking("UploadCsv")
    },
    error: (res) => {
      console.log("[parsingCSV] Error: ", res)
      callbacks.onError(CSVErrors.INVALID_TYPE)
    }
  })
}
